import {
  Component,
  OnInit,
  AfterViewInit,
  ViewChild,
  ElementRef,
  OnDestroy,
  EventEmitter,
  HostListener,
  ChangeDetectorRef,
  Inject
} from '@angular/core';
import {
  FormGroup,
  FormBuilder
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, Subscription } from 'rxjs';
import { AwsAuthService } from '@nx/auth';
import { parsePhoneNumber, CountryCode } from 'libphonenumber-js';
import { AuthProcessService } from '../../../common/services/auth-process.service';
import { SignInUserError, SignInUserErrorAction } from '../../store/sign-in.actions';
import { SignInStore } from '../../interfaces/sign-in-store.interface';
import { signInUsernameState, signInForgottenPasswordState } from '../../routing/route-locales/sign-in-routes';
import { countryCodeISO } from '../../../providers/country-code';
import { AuthProcessProvider } from '../../../providers/auth-process-provider';
import { Actions, ofType } from '@ngrx/effects';
import { SignInService } from '../../services/sign-in.service';
import { SignInCms } from '../../interfaces/sign-in-cms.interface';

@Component({
  selector: 'auth-process-sign-in-password',
  templateUrl: './sign-in-password.component.html',
})
export class SignInPasswordComponent
  implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('passwordForm') passwordForm: ElementRef;
  @ViewChild('passwordInput') passwordInput: ElementRef;
  @ViewChild('sendResetCodeSlider') sendResetCodeSlider: ElementRef;
  @ViewChild('footer') footer: ElementRef;

  private readonly halfSecondDelay: number = 500;
  private readonly signInForgottenPasswordStage = `../${signInForgottenPasswordState.path}`;

  public signInPasswordForm: FormGroup;
  public canNavigate = false;
  public disableSendButton = false;
  public showPassword = false;

  public slideTopPosition: number;
  public finalFeedbackPosition: number;

  public showFeedbackCallout = '';

  public signInEmail = false;
  public phoneNumberWithoutCountryCodeForDisplay: string;
  public signInAttribute: string;
  public showResetCodeSlider = false;
  public showSignInLoader: Observable<
    boolean
  > = this.signInService.selectSignInPending();
  public showSendingLoader = false;
  public removeHelpMessage: EventEmitter<string> = new EventEmitter();
  public signInUserErrorAction: string = SignInUserErrorAction;
  public moveFooterUnderPasswordErrorHelp = false;
  public signInUsernameStage = `../${signInUsernameState.path}`;
  public _passwordInput: ElementRef;
  public signInCms: SignInCms;

  private inputHasFocus = false;
  private passwordEntered: string;
  private formSubscription: Subscription;

  @HostListener('window:resize', ['$event'])
  onResize(event) {
    if (!this.showResetCodeSlider) {
      this.preventSliderFromPeakingOutOnResize();
    } else {
      this.calculateFeedbackSliderPosition();
      this.setTopOfSliderPosition();
    }
  }

  constructor(
    public authProcessProvider: AuthProcessProvider,
    private formBuilder: FormBuilder,
    private route: ActivatedRoute,
    private authProcessService: AuthProcessService,
    private awsAuthService: AwsAuthService,
    private signInService: SignInService,
    private router: Router,
    private changeDetector: ChangeDetectorRef,
    private actions: Actions,
    @Inject(countryCodeISO) private countryCodeISO: CountryCode
  ) {}

  ngOnInit(): void {
    this.signInCms = this.route.snapshot.data.signInCmsData;
    this.createForm();
    this.subscribeToFormValueChanges();
    this.selectUsernameEmailOrPhoneNumberData(
      this.route.snapshot.data.signInDetails
    );
    this.subscribeToActionErrors();
  }

  ngAfterViewInit(): void {
    this.setTopOfSliderPosition();
    this.calculateFeedbackSliderPosition();
    this.changeDetector.detectChanges();
  }

  ngAfterContentChecked() {
    this._passwordInput = this.passwordInput;
  }

  public removeCountryCodeFromNumber(number:string): string {
    const phoneNumber = parsePhoneNumber(number, this.countryCodeISO)
    return phoneNumber.formatNational();
  }

  public showPasswordToggle(show: boolean) {
    this.showPassword = show;
    if (this.inputHasFocus) {
      this.focusInputOnPasswordInput();
    }
  }

  public doneButtonClicked() {
    this.passwordEntered = this.signInPasswordForm.value.password;
    this.canNavigate = true;
  }

  public signInButtonClicked() {
    this.canNavigate = false;
    this.signUserIn();
  }

  public signUserIn() {
    this.moveFooterUnderPasswordErrorHelp = true;
    this.showResetCodeSlider = false;
    this.signInService.dispatchSignInUser(
      this.signInAttribute,
      this.passwordEntered,
      { app: 'lmw' }
    );
  }

  public preventFocusOnElement(event) {
    event.preventDefault();
  }

  public helpMessageClose() {
    this.focusInputOnPasswordInput();
  }

  public inputBlurred() {
    this.inputHasFocus = false;
  }

  public onPasswordInputFocus() {
    this.inputHasFocus = true;
    this.removeHelpMessageEmit();
  }

  public showCodeSlider() {
    this.removeHelpMessageEmit();
    this.setTopOfSliderPosition();
    this.showResetCodeSlider = true;
  }

  public hideCodeSlider() {
    this.showResetCodeSlider = false;
    this.showSendingLoader = false;
    this.disableSendButton = false;
  }

  public sendPasswordCode() {
    this.disableSendButton = true;
    this.showSendingLoader = true;
    this.awsAuthService
      .sendResetPasswordCode(this.signInAttribute, { app: "lmw", appVersion: this.authProcessProvider.appVersion })
      .subscribe(() => {
        this.goToForgottenPassword();
      }, (error) => {
        if (error.message === 'LimitExceededException') {
          this.showCorrectFeedbackCallout('tooManyCodeAttempts');
          this.enableCodeButtonsAfterDelay();
        }
      });
  }

  private subscribeToActionErrors() {
    this.actions.pipe(ofType(SignInUserError)).subscribe(action =>
      action.error.message === 'Password attempts exceeded' ? this.showCorrectFeedbackCallout('tooManyPasswordAttempts') : ''
    );
  }

  private goToForgottenPassword() {
    setTimeout(() => {
      this.router.navigate([this.signInForgottenPasswordStage], {
        relativeTo: this.route,
      });
    }, this.halfSecondDelay);
  }

  private showCorrectFeedbackCallout(callout) {
    this.showFeedbackCallout = callout;
    this.removeHelpMessageEmit();
    setTimeout(() => {
      this.showFeedbackCallout = '';
    }, this.halfSecondDelay * 20);
  }

  private enableCodeButtonsAfterDelay() {
    setTimeout(() => {
      this.showSendingLoader = false;
      this.disableSendButton = false;
    }, this.halfSecondDelay * 20);
  }

  private calculateFeedbackSliderPosition() {
    if (window.innerWidth < 640) {
      this.finalFeedbackPosition = 0;
    } else {
      this.finalFeedbackPosition = this.footer.nativeElement.offsetHeight * -1;
    }
  }

  private selectUsernameEmailOrPhoneNumberData(signInStore: SignInStore) {
    if (signInStore.email !== '') {
      this.signInEmail = true;
      this.signInAttribute = signInStore.email;
    } else if (signInStore.phoneNumber !== '') {
      this.signInEmail = false;
      this.signInAttribute = signInStore.phoneNumber;
      this.phoneNumberWithoutCountryCodeForDisplay = this.removeCountryCodeFromNumber(
        this.signInAttribute
      );
    }
  }

  private preventSliderFromPeakingOutOnResize() {
    const sliderTopOffscreen = -1000;
    this.slideTopPosition = sliderTopOffscreen;
    setTimeout(() => {
      this.setTopOfSliderPosition();
    }, this.halfSecondDelay / 4);
  }

  private setTopOfSliderPosition() {
    const sliderShadowSizeOutsideBoxModel = 2;
    this.slideTopPosition =
      (this.sendResetCodeSlider.nativeElement.offsetHeight + sliderShadowSizeOutsideBoxModel) *
      -1;
  }

  private removeHelpMessageEmit() {
    this.moveFooterUnderPasswordErrorHelp = false;
    this.removeHelpMessage.next(this.signInUserErrorAction);
  }

  private createForm() {
    this.signInPasswordForm = this.formBuilder.group({});
  }

  private focusInputOnPasswordInput() {
    this.authProcessService.setCursorToEndOfInput('password');
  }

  private subscribeToFormValueChanges() {
    this.formSubscription = this.signInPasswordForm.valueChanges.subscribe(
      (password) => {
        if (password) {
          if (
            this.passwordEntered !==
            this.signInPasswordForm.get('password').value
          ) {
            this.canNavigate = false;
          }
        }
      }
    );
  }

  ngOnDestroy(): void {
    this.formSubscription.unsubscribe();
  }
}
